import type { DateTime } from 'luxon';
import { ExperienceTimeFormat } from '@/types/gql.generated';
import { areDatesSame, hasSameMeridiem } from './dates';

export const TIME_FORMAT_TOKENS: Record<ExperienceTimeFormat, string> = {
  [ExperienceTimeFormat.TwelveHour]: 'h:mm a',
  [ExperienceTimeFormat.TwelveHourNoMeridiem]: 'h:mm',
  // Compact's single-letter meridiem is applied in renderExact; the token here
  // is only the fallback for multi-day date summaries, where 'h:mm' reads fine.
  [ExperienceTimeFormat.TwelveHourCompact]: 'h:mm',
  [ExperienceTimeFormat.HourOnly]: 'h a',
  [ExperienceTimeFormat.TwentyFourHour]: 'HH:mm',
};

/**
 * The luxon format token for a given time format. Defaults to 12-hour so
 * callers that don't opt into the per-view enum (e.g. the legacy Page/Schedule
 * product) keep their existing behavior. Retained for the multi-day date
 * summaries that embed a time inside a date string.
 */
export const getTimeFormatToken = (timeFormat?: ExperienceTimeFormat): string =>
  TIME_FORMAT_TOKENS[timeFormat ?? ExperienceTimeFormat.TwelveHour];

// Range separator used by the enum-driven (WYSIWYG) output — an en dash, to
// mirror the format-picker labels (e.g. "9:00 am – 3:00 pm").
const EN_DASH = '–';

// Legacy compact styling (Page/Schedule): drop a zero-minute component and
// lowercase the meridiem. Applied only when no explicit format is chosen.
const legacyStyle = (formatted: string): string =>
  formatted.replace(/:00/g, '').toLowerCase();

/**
 * Render a single time exactly as its format's picker label reads — no
 * minute-dropping (except the no-meridiem format, whose label shows bare whole
 * hours) and no meridiem suppression. This is the WYSIWYG path used whenever a
 * per-view format is explicitly chosen.
 */
const renderExact = (
  date: DateTime,
  timeFormat: ExperienceTimeFormat
): string => {
  switch (timeFormat) {
    case ExperienceTimeFormat.TwelveHour:
      return date.toFormat('h:mm a').toLowerCase(); // 9:00 am
    case ExperienceTimeFormat.TwelveHourNoMeridiem:
      return date.toFormat('h:mm').replace(/:00$/, ''); // 9 / 9:30
    case ExperienceTimeFormat.HourOnly:
      return date.toFormat('h a').toLowerCase(); // 9 am
    case ExperienceTimeFormat.TwentyFourHour:
      return date.toFormat('HH:mm'); // 09:00
    case ExperienceTimeFormat.TwelveHourCompact: {
      // 12-hour, single-letter meridiem, zero minutes dropped: 9a / 9:30a / 3p
      const meridiem = date.hour < 12 ? 'a' : 'p';
      return `${date.toFormat('h:mm').replace(/:00$/, '')}${meridiem}`;
    }
    default: {
      // Exhaustiveness guard: a new enum value must add a case above.
      const _exhaustive: never = timeFormat;
      return _exhaustive;
    }
  }
};

/**
 * Format a single time. With an explicit `timeFormat` the output mirrors the
 * picker label exactly (WYSIWYG); without one it falls back to the legacy
 * 12-hour compact styling so Page/Schedule are unaffected.
 */
export const formatTime = (
  date: DateTime,
  timeFormat?: ExperienceTimeFormat
): string =>
  timeFormat === undefined
    ? legacyStyle(
        date.toFormat(TIME_FORMAT_TOKENS[ExperienceTimeFormat.TwelveHour])
      )
    : renderExact(date, timeFormat);

/**
 * Format a start–end time range. With an explicit `timeFormat` each end is
 * rendered exactly per its label and joined with an en dash (no meridiem
 * suppression), so the event mirrors the picker. Without a format it keeps the
 * legacy compact behavior: drop `:00`, suppress the start meridiem when both
 * share one, hyphen separator.
 */
export const formatTimeRange = (
  start: DateTime,
  end: DateTime,
  timeFormat?: ExperienceTimeFormat,
  { includeEndTime = true }: { includeEndTime?: boolean } = {}
): string => {
  if (timeFormat === undefined) {
    const token = TIME_FORMAT_TOKENS[ExperienceTimeFormat.TwelveHour];
    if (!includeEndTime || areDatesSame(start, end)) {
      return legacyStyle(start.toFormat(token));
    }
    const startToken = hasSameMeridiem(start, end)
      ? token.replace(/ a$/, '')
      : token;
    return `${legacyStyle(start.toFormat(startToken))} - ${legacyStyle(
      end.toFormat(token)
    )}`;
  }

  if (!includeEndTime || areDatesSame(start, end)) {
    return renderExact(start, timeFormat);
  }
  const startLabel = renderExact(start, timeFormat);
  const endLabel = renderExact(end, timeFormat);
  // Lossy formats (hour-only, whole-hour compact) can render two distinct times
  // in the same meridiem identically — collapse to one rather than "9 am – 9 am".
  // Require the same meridiem so the no-meridiem format doesn't collapse a real
  // cross-noon span (9:00 and 21:00 both render "9" → keep "9 – 9", not "9").
  return startLabel === endLabel && hasSameMeridiem(start, end)
    ? startLabel
    : `${startLabel} ${EN_DASH} ${endLabel}`;
};
